import { Typography } from '@/components/atoms';
import { useUIColors } from '@/core/hooks';
import { useScrollPagination } from '@/modules/properties-web/hooks/useScrollPagination';
import React from 'react';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  View,
  type ImageSourcePropType,
} from 'react-native';
import type {
  GuestDetailsLayout,
  GuestDetailsTypeScale,
} from '../constants/guestLayout';
import type { GuestLabels } from '../constants/getGuestLabels';
import type {
  GuestListLoadState,
  GuestPropertyCardModel,
  GuestUnitDetailsModel,
} from '../types/guest.types';
import { GuestExploreList } from './GuestExploreList';
import { GuestListEmptyState } from './GuestListEmptyState';
import { GuestUnitDetailsMainColumn } from './GuestUnitDetailsMainColumn';
import { GuestDetailsScreenSkeleton } from './skeleton';

export type CompactDetailsTab = 'details' | 'explore';

export interface GuestUnitDetailsLayoutShellProps {
  readonly loadState: GuestListLoadState;
  readonly unit: GuestUnitDetailsModel | null;
  readonly unitImages: ReadonlyArray<ImageSourcePropType>;
  readonly exploreProperties: ReadonlyArray<GuestPropertyCardModel>;
  readonly canLoadMoreExplore: boolean;
  readonly isFetchingNextExplorePage: boolean;
  readonly onLoadMoreExplore: () => void;
  readonly layout: GuestDetailsLayout;
  readonly typeScale: GuestDetailsTypeScale;
  readonly labels: GuestLabels;
  readonly pagePadStyle: Readonly<{
    paddingHorizontal: number;
    paddingTop: number;
  }>;
  readonly compactTab: CompactDetailsTab;
  readonly compactScrollRef: React.RefObject<ScrollView | null>;
  readonly onSelectCompactTab: (tab: CompactDetailsTab) => void;
  readonly onMapPress: () => void;
  readonly onDocumentPress: (documentId: string) => void;
  readonly downloadingDocumentId?: string | null;
  readonly onExplorePress: (id: string) => void;
}

function CompactDetailsTabs({
  compactTab,
  isPhone,
  detailsLabel,
  exploreLabel,
  onSelect,
}: Readonly<{
  compactTab: CompactDetailsTab;
  isPhone: boolean;
  detailsLabel: string;
  exploreLabel: string;
  onSelect: (tab: CompactDetailsTab) => void;
}>) {
  const colors = useUIColors();
  const detailsSelected = compactTab === 'details';
  const exploreSelected = compactTab === 'explore';
  const tabVariant = isPhone ? 'subhead' : 'headline';

  return (
    <View style={styles.compactTabs}>
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ selected: detailsSelected }}
        onPress={() => onSelect('details')}
        style={[
          styles.compactTab,
          {
            backgroundColor: detailsSelected
              ? colors.buttonDefault
              : colors.backgroundLevel1,
          },
        ]}
      >
        <Typography
          variant={tabVariant}
          color={detailsSelected ? 'white' : 'primary'}
          style={styles.compactTabText}
        >
          {detailsLabel}
        </Typography>
      </Pressable>
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ selected: exploreSelected }}
        onPress={() => onSelect('explore')}
        style={[
          styles.compactTab,
          {
            backgroundColor: exploreSelected
              ? colors.buttonDefault
              : colors.backgroundLevel1,
          },
        ]}
      >
        <Typography
          variant={tabVariant}
          color={exploreSelected ? 'white' : 'primary'}
          style={styles.compactTabText}
        >
          {exploreLabel}
        </Typography>
      </Pressable>
    </View>
  );
}

function DesktopSplitLayout({
  unit,
  unitImages,
  exploreProperties,
  isFetchingNextExplorePage,
  exploreScrollPagination,
  layout,
  typeScale,
  labels,
  pagePadStyle,
  onMapPress,
  onDocumentPress,
  downloadingDocumentId = null,
  onExplorePress,
}: Readonly<{
  unit: GuestUnitDetailsModel;
  unitImages: ReadonlyArray<ImageSourcePropType>;
  exploreProperties: ReadonlyArray<GuestPropertyCardModel>;
  isFetchingNextExplorePage: boolean;
  exploreScrollPagination: ReturnType<typeof useScrollPagination>;
  layout: GuestDetailsLayout;
  typeScale: GuestDetailsTypeScale;
  labels: GuestLabels;
  pagePadStyle: Readonly<{
    paddingHorizontal: number;
    paddingTop: number;
  }>;
  onMapPress: () => void;
  onDocumentPress: (documentId: string) => void;
  downloadingDocumentId?: string | null;
  onExplorePress: (id: string) => void;
}>) {
  return (
    <View style={[styles.splitShell, pagePadStyle]}>
      <ScrollView
        style={styles.mainScroll}
        contentContainerStyle={styles.mainScrollContent}
        showsVerticalScrollIndicator={false}
      >
        <GuestUnitDetailsMainColumn
          unit={unit}
          unitImages={unitImages}
          layout={layout}
          typeScale={typeScale}
          labels={labels}
          onMapPress={onMapPress}
          onDocumentPress={onDocumentPress}
          downloadingDocumentId={downloadingDocumentId}
        />
      </ScrollView>
      <View style={[styles.sidebar, styles.sidebarPane]}>
        <ScrollView
          style={styles.sidebarScroll}
          contentContainerStyle={styles.sidebarScrollContent}
          showsVerticalScrollIndicator={false}
          nestedScrollEnabled
          {...exploreScrollPagination}
        >
          <GuestExploreList
            properties={exploreProperties}
            emptyTitle={labels.exploreEmpty}
            exploreTitle={labels.exploreProperties}
            titleVariant={typeScale.pageTitle}
            showTitle
            isFetchingNextPage={isFetchingNextExplorePage}
            onPropertyPress={onExplorePress}
          />
        </ScrollView>
      </View>
    </View>
  );
}

function CompactTabLayout({
  loadState,
  unit,
  unitImages,
  exploreProperties,
  isFetchingNextExplorePage,
  exploreScrollPagination,
  layout,
  typeScale,
  labels,
  pagePadStyle,
  compactTab,
  compactScrollRef,
  onSelectCompactTab,
  onMapPress,
  onDocumentPress,
  downloadingDocumentId = null,
  onExplorePress,
}: Readonly<{
  loadState: GuestListLoadState;
  unit: GuestUnitDetailsModel | null;
  unitImages: ReadonlyArray<ImageSourcePropType>;
  exploreProperties: ReadonlyArray<GuestPropertyCardModel>;
  isFetchingNextExplorePage: boolean;
  exploreScrollPagination: ReturnType<typeof useScrollPagination>;
  layout: GuestDetailsLayout;
  typeScale: GuestDetailsTypeScale;
  labels: GuestLabels;
  pagePadStyle: Readonly<{
    paddingHorizontal: number;
    paddingTop: number;
  }>;
  compactTab: CompactDetailsTab;
  compactScrollRef: React.RefObject<ScrollView | null>;
  onSelectCompactTab: (tab: CompactDetailsTab) => void;
  onMapPress: () => void;
  onDocumentPress: (documentId: string) => void;
  downloadingDocumentId?: string | null;
  onExplorePress: (id: string) => void;
}>) {
  const showError = loadState === 'error' || loadState === 'empty';
  const showReady = loadState === 'ready' && unit != null;
  const explorePaginationProps =
    compactTab === 'explore' ? exploreScrollPagination : undefined;

  return (
    <ScrollView
      ref={compactScrollRef}
      style={styles.scroll}
      contentContainerStyle={[
        styles.scrollContent,
        pagePadStyle,
        { gap: layout.isPhone ? 12 : 16 },
      ]}
      showsVerticalScrollIndicator={false}
      {...explorePaginationProps}
    >
      {loadState === 'loading' ? (
        <GuestDetailsScreenSkeleton layout={layout} />
      ) : null}

      {showError ? (
        <GuestListEmptyState title={labels.emptyFetchError} />
      ) : null}

      {showReady && unit ? (
        <View style={[styles.body, styles.bodyStack]}>
          <CompactDetailsTabs
            compactTab={compactTab}
            isPhone={layout.isPhone}
            detailsLabel={labels.unitDetails}
            exploreLabel={labels.exploreProperties}
            onSelect={onSelectCompactTab}
          />

          {compactTab === 'details' ? (
            <GuestUnitDetailsMainColumn
              unit={unit}
              unitImages={unitImages}
              layout={layout}
              typeScale={typeScale}
              labels={labels}
              onMapPress={onMapPress}
              onDocumentPress={onDocumentPress}
              downloadingDocumentId={downloadingDocumentId}
            />
          ) : null}

          {compactTab === 'explore' ? (
            <View style={[styles.sidebar, styles.sidebarCompact]}>
              <GuestExploreList
                properties={exploreProperties}
                emptyTitle={labels.exploreEmpty}
                exploreTitle={labels.exploreProperties}
                titleVariant={typeScale.pageTitle}
                showTitle={false}
                isFetchingNextPage={isFetchingNextExplorePage}
                onPropertyPress={onExplorePress}
              />
            </View>
          ) : null}
        </View>
      ) : null}
    </ScrollView>
  );
}

/**
 * Desktop split panes vs compact Details | Explore tabs.
 */
export function GuestUnitDetailsLayoutShell({
  loadState,
  unit,
  unitImages,
  exploreProperties,
  canLoadMoreExplore,
  isFetchingNextExplorePage,
  onLoadMoreExplore,
  layout,
  typeScale,
  labels,
  pagePadStyle,
  compactTab,
  compactScrollRef,
  onSelectCompactTab,
  onMapPress,
  onDocumentPress,
  downloadingDocumentId = null,
  onExplorePress,
}: Readonly<GuestUnitDetailsLayoutShellProps>) {
  const exploreScrollPagination = useScrollPagination({
    onLoadMore: onLoadMoreExplore,
    enabled: canLoadMoreExplore && loadState === 'ready',
  });

  const showSplitScroll =
    loadState === 'ready' && unit != null && !layout.isCompact;

  if (showSplitScroll && unit) {
    return (
      <DesktopSplitLayout
        unit={unit}
        unitImages={unitImages}
        exploreProperties={exploreProperties}
        isFetchingNextExplorePage={isFetchingNextExplorePage}
        exploreScrollPagination={exploreScrollPagination}
        layout={layout}
        typeScale={typeScale}
        labels={labels}
        pagePadStyle={pagePadStyle}
        onMapPress={onMapPress}
        onDocumentPress={onDocumentPress}
        downloadingDocumentId={downloadingDocumentId}
        onExplorePress={onExplorePress}
      />
    );
  }

  return (
    <CompactTabLayout
      loadState={loadState}
      unit={unit}
      unitImages={unitImages}
      exploreProperties={exploreProperties}
      isFetchingNextExplorePage={isFetchingNextExplorePage}
      exploreScrollPagination={exploreScrollPagination}
      layout={layout}
      typeScale={typeScale}
      labels={labels}
      pagePadStyle={pagePadStyle}
      compactTab={compactTab}
      compactScrollRef={compactScrollRef}
      onSelectCompactTab={onSelectCompactTab}
      onMapPress={onMapPress}
      onDocumentPress={onDocumentPress}
      downloadingDocumentId={downloadingDocumentId}
      onExplorePress={onExplorePress}
    />
  );
}

const styles = StyleSheet.create({
  scroll: {
    flex: 1,
    width: '100%',
    minHeight: 0,
  },
  scrollContent: {
    paddingBottom: 96,
    width: '100%',
    maxWidth: '100%',
    flexGrow: 1,
  },
  splitShell: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 24,
    width: '100%',
    minWidth: 0,
    minHeight: 0,
    overflow: 'hidden',
  },
  mainScroll: {
    flexGrow: 729,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
    minHeight: 0,
  },
  mainScrollContent: {
    flexGrow: 0,
    paddingBottom: 96,
    gap: 16,
  },
  body: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
  },
  bodyStack: {
    flexDirection: 'column',
    gap: 24,
  },
  compactTabs: {
    flexDirection: 'row',
    gap: 8,
    width: '100%',
  },
  compactTab: {
    flex: 1,
    minHeight: 44,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  compactTabText: {
    letterSpacing: 0,
    textAlign: 'center',
  },
  sidebar: {
    flexGrow: 442,
    flexShrink: 1,
    flexBasis: 0,
    gap: 16,
    minWidth: 0,
    minHeight: 0,
    maxWidth: '100%',
  },
  sidebarPane: {
    overflow: 'hidden',
    minHeight: 0,
  },
  sidebarScroll: {
    flex: 1,
    width: '100%',
    minHeight: 0,
  },
  sidebarScrollContent: {
    flexGrow: 0,
    gap: 16,
    paddingBottom: 96,
  },
  sidebarCompact: {
    flexGrow: 0,
    flexShrink: 1,
    flexBasis: 'auto',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
  },
});

export default GuestUnitDetailsLayoutShell;
